export const WEEKDAY_CONFIG = [
    { key: "mon", label: "Monday" },
    { key: "tue", label: "Tuesday" },
    { key: "wed", label: "Wednesday" },
    { key: "thu", label: "Thursday" },
    { key: "fri", label: "Friday" },
    { key: "sat", label: "Saturday" },
    { key: "sun", label: "Sunday" }
];

export const createEmptyRegistrationSchedule = () =>
    WEEKDAY_CONFIG.reduce((acc, day) => {
        acc[day.key] = [];
        return acc;
    }, {});

export const normalizeRegistrationSchedule = (schedule) => {
    if (!schedule) return createEmptyRegistrationSchedule();
    if (typeof schedule === "string") {
        try {
            const parsed = JSON.parse(schedule);
            return normalizeRegistrationSchedule(parsed);
        } catch (error) {
            return createEmptyRegistrationSchedule();
        }
    }
    if (typeof schedule !== "object" || Array.isArray(schedule)) {
        return createEmptyRegistrationSchedule();
    }

    const normalized = createEmptyRegistrationSchedule();
    WEEKDAY_CONFIG.forEach(({ key }) => {
        const dayIntervals = schedule[key];
        normalized[key] = Array.isArray(dayIntervals)
            ? dayIntervals
                .map((interval) => {
                    if (interval?.allDay === true) {
                        return { allDay: true };
                    }
                    return {
                        start: interval?.start || "",
                        end: interval?.end || ""
                    };
                })
                .filter((interval) => interval.allDay || interval.start || interval.end)
            : [];
    });

    return normalized;
};

const toMinutes = (timeValue) => {
    if (typeof timeValue !== "string") return null;
    const match = timeValue.match(/^([01]\d|2[0-3]):([0-5]\d)$/);
    if (!match) return null;
    return parseInt(match[1], 10) * 60 + parseInt(match[2], 10);
};

export const validateRegistrationSchedule = (schedule) => {
    const normalized = normalizeRegistrationSchedule(schedule);
    const errors = {};
    let isValid = true;

    WEEKDAY_CONFIG.forEach(({ key }) => {
        const intervals = normalized[key] || [];
        const dayErrors = [];
        const hasAllDay = intervals.some((interval) => interval.allDay === true);

        if (hasAllDay && intervals.length > 1) {
            dayErrors.push("All day cannot be combined with extra intervals.");
        }

        const parsedIntervals = intervals
            .filter((interval) => !interval.allDay)
            .map((interval, index) => {
                const startMinutes = toMinutes(interval.start);
                const endMinutes = toMinutes(interval.end);
                if (startMinutes === null || endMinutes === null) {
                    dayErrors.push(`Interval ${index + 1} must have valid start/end time.`);
                    return null;
                }
                if (startMinutes >= endMinutes) {
                    dayErrors.push(`Interval ${index + 1} start time must be earlier than end time.`);
                    return null;
                }
                return { startMinutes, endMinutes };
            })
            .filter(Boolean)
            .sort((a, b) => a.startMinutes - b.startMinutes);

        for (let i = 1; i < parsedIntervals.length; i += 1) {
            if (parsedIntervals[i].startMinutes < parsedIntervals[i - 1].endMinutes) {
                dayErrors.push("Intervals cannot overlap.");
                break;
            }
        }

        if (dayErrors.length > 0) {
            errors[key] = dayErrors.join(" ");
            isValid = false;
        }
    });

    return { isValid, errors, normalized };
};

export const formatRegistrationScheduleForDisplay = (hasRegistrationSchedule, schedule) => {
    if (!hasRegistrationSchedule) return [];
    const normalized = normalizeRegistrationSchedule(schedule);

    return WEEKDAY_CONFIG.flatMap(({ key, label }) => {
        const intervals = normalized[key] || [];
        if (intervals.length === 0) return [];
        if (intervals.some((interval) => interval.allDay)) {
            return `${label}: all day`;
        }
        return `${label}: ${intervals.map((interval) => `${interval.start}-${interval.end}`).join(", ")}`;
    });
};
